import React from 'react';
import { Lock } from 'lucide-react';

const AICouncilSection = () => {
  const coaches = [
    {
      name: 'Vera',
      emoji: '🌿',
      title: 'Financial Wellness Coach',
      description: 'Warm, encouraging, and holistic. Vera is your daily financial companion from day one — budgeting, goals, insights, and everything in between.',
      example: null,
      status: 'Always Available',
      statusColor: 'bg-[#2ECC71]',
      locked: false,
      image: 'https://customer-assets-4nw71qhi.emergentagent.net/job_financial-transform/artifacts/mw73m2ce_findell_coach_vera_FINAL.png',
    },
    {
      name: 'Rex',
      emoji: '💪',
      title: 'The Debt Destroyer',
      description: 'Direct, tactical, and results-focused. Rex specializes in debt payoff strategies and will build your personalized debt destruction plan.',
      example: '"My plan gets you debt-free in 2.5 years instead of 6. Want to see it?"',
      status: 'Unlocks with significant debt',
      statusColor: 'bg-[#F39C12]',
      locked: true,
      image: 'https://customer-assets-4nw71qhi.emergentagent.net/job_financial-transform/artifacts/gr3gf417_findell_coach_rex_FINAL.png',
    },
    {
      name: 'Nova',
      emoji: '📈',
      title: 'The Investment Guide',
      description: 'Analytical and educational. Nova makes investing simple and accessible — from index funds to retirement planning.',
      example: '"Your $1,000 is losing to inflation. Let me show you three beginner options."',
      status: 'Unlocks at Level 5',
      statusColor: 'bg-[#D4AF37]',
      locked: true,
      image: 'https://customer-assets-4nw71qhi.emergentagent.net/job_financial-transform/artifacts/rao1g3dn_findell_coach_nova_FINAL.png',
    },
  ];

  return (
    <section
      data-testid="ai-council-section"
      className="bg-[#0B1F17] section-padding relative overflow-hidden"
    >
      {/* Ambient glow background */}
      <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-[#D4AF37]/10 rounded-full blur-[100px] animate-ambient-glow" />
      <div className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-[#95B8A2]/10 rounded-full blur-[80px] animate-ambient-glow" style={{ animationDelay: '2s' }} />
      
      <div className="container-custom relative z-10">
        {/* Section Header */}
        <div className="text-center mb-16">
          <p
            data-testid="council-section-label"
            className="font-body text-xs sm:text-sm font-semibold tracking-[0.2em] uppercase text-[#D4AF37] mb-4"
          >
            Three Coaches. One App.
          </p>
          <h2
            data-testid="council-section-headline"
            className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-[#FAFAF7] leading-tight"
          >
            Every Stage of Your Financial
            <br />
            Journey Has a Coach.
          </h2>
        </div>

        {/* Coach Cards - Heritage Glassmorphism */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
          {coaches.map((coach, index) => (
            <div
              key={coach.name}
              data-testid={`coach-card-${coach.name.toLowerCase()}`}
              className="glass-heritage rounded-2xl overflow-hidden animate-fade-in-up transition-all duration-500 hover:-translate-y-2 hover:shadow-[0_0_50px_rgba(212,175,55,0.2)]"
              style={{ animationDelay: `${index * 0.15}s` }}
            >
              <div className="p-6">
                {/* Avatar with gold glow ring */}
                <div className="flex justify-center mb-6">
                  <div className="relative">
                    <div className="w-24 h-24 rounded-full overflow-hidden border-2 border-[#D4AF37]/40 shadow-[0_0_20px_rgba(212,175,55,0.3)] animate-gold-glow">
                      <img
                        src={coach.image}
                        alt={`${coach.name} - ${coach.title}`}
                        className="w-full h-full object-cover"
                        loading="lazy"
                      />
                    </div>
                    <span className="absolute -bottom-1 -right-1 text-2xl bg-[#0B1F17]/80 backdrop-blur-sm rounded-full w-10 h-10 flex items-center justify-center border border-[#D4AF37]/30">
                      {coach.emoji}
                    </span>
                  </div>
                </div>

                {/* Name & Title */}
                <div className="text-center mb-4">
                  <h3 className="font-heading text-2xl font-bold text-[#FAFAF7] mb-1">
                    {coach.name}
                  </h3>
                  <p className="font-body text-sm text-[#D4AF37]">
                    {coach.title}
                  </p>
                </div>

                {/* Description */}
                <p className="font-body text-sm text-[#FAFAF7]/80 leading-relaxed text-center mb-4">
                  {coach.description}
                </p>

                {/* Example Quote */}
                {coach.example && (
                  <div className="bg-[#D4AF37]/10 border border-[#D4AF37]/20 rounded-xl p-3 mb-4">
                    <p className="font-body text-sm italic text-[#FAFAF7]/90 text-center">
                      {coach.example}
                    </p>
                  </div>
                )}

                {/* Status Badge */}
                <div className="flex justify-center">
                  <span
                    className={`inline-flex items-center gap-2 px-4 py-2 bg-[#D4AF37]/10 border border-[#D4AF37]/20 rounded-full`}
                  >
                    {coach.locked && <Lock className="w-3 h-3 text-[#D4AF37]" />}
                    <span className="font-body text-xs font-medium text-[#FAFAF7]/80">
                      {coach.status}
                    </span>
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Note about placeholder images */}
        <p className="text-center mt-8 font-body text-xs text-[#FAFAF7]/40">
          Coach avatars are AI-generated fictional characters — no real individuals depicted.
        </p>
      </div>
    </section>
  );
};

export default AICouncilSection;
